import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'

// Deal stages for collections
const STAGES = {
  AWAITING_SHIPPING_LABEL: '597442256',
  AWAITING_COLLECTION: '4367683815',
  SHIPPING_ARRANGED: 'presentationscheduled',
  QUOTE_SENT: 'qualifiedtobuy',
  PROBLEM_WITH_SHIPPING: '589142218'
}

// Carriers with details
const CARRIERS = {
  royal_mail: { id: 'royal_mail', name: 'Royal Mail', color: '#E74C3C', maxWeight: 10, maxBoxes: 5, conditions: 'Single box ≤10kg (20kg NI), max 61x46x46cm', url: 'https://send.royalmail.com/login?returnUrl=%2F' },
  parcelforce: { id: 'parcelforce', name: 'ParcelForce', color: '#3498DB', maxWeight: 30, conditions: 'Up to 30kg, max 150cm longest side', url: 'https://send.parcelforce.com' },
  fedex: { id: 'fedex', name: 'FedEx', color: '#4D148C', conditions: 'Heavy/multiple boxes with printer', url: 'https://www.fedex.com/shippingplus/en-gb/' },
  shiply: { id: 'shiply', name: 'Shiply', color: '#F1C40F', conditions: 'Large/bulky items >70kg or >150cm', url: 'https://www.shiply.com/listings/user_active' }
}

// =============================================================================
// TRACKING URL HELPER
// =============================================================================

function getTrackingUrl(trackingNumber, carrier) {
  if (!trackingNumber || !carrier) return null
  const carrierLower = carrier.toLowerCase()

  if (carrierLower.includes('royal')) {
    return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
  }
  if (carrierLower.includes('parcel')) {
    if (trackingNumber.toUpperCase().startsWith('PFW')) {
      return `https://send.parcelforce.com/tracking/${trackingNumber.toLowerCase()}`
    }
    return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
  }
  if (carrierLower.includes('fedex')) {
    return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
  }
  if (carrierLower.includes('dhl')) {
    return `https://www.dhl.com/gb-en/home/tracking.html?tracking-id=${trackingNumber}`
  }
  return null
}

// Extract just the tracking number from a URL or text
function extractTrackingNumber(input) {
  if (!input) return ''
  // If it's already just a number/code, return it
  if (!input.includes('http') && !input.includes('/')) {
    return input.trim().toUpperCase()
  }
  // Try to extract from common tracking URLs
  const patterns = [
    /tracking-results\/([A-Z0-9]+)/i,
    /tracking\/([A-Z0-9]+)/i,
    /trknbr=([A-Z0-9]+)/i,
    /tracking-id=([A-Z0-9]+)/i,
    /([A-Z]{2}\d{9}GB)/i, // Royal Mail format
    /(PF[A-Z]?\d+)/i, // ParcelForce format
    /(\d{12,15})/  // FedEx format
  ]
  for (const pattern of patterns) {
    const match = input.match(pattern)
    if (match) return match[1].toUpperCase()
  }
  return input.trim().toUpperCase()
}

// =============================================================================
// CARRIER ASSIGNMENT LOGIC
// =============================================================================

function isNorthernIreland(postcode) {
  if (!postcode) return false
  const prefix = postcode.toUpperCase().replace(/\s/g, '').substring(0, 2)
  return prefix === 'BT'
}

function fitsRoyalMailDimensions(box) {
  if (!box) return false
  const dims = [box.length || 0, box.width || 0, box.height || box.depth || 0].sort((a, b) => b - a)
  return dims[0] <= 61 && dims[1] <= 46 && dims[2] <= 46
}

function allBoxesFitRoyalMailDimensions(boxes) {
  if (!boxes || boxes.length === 0) return false
  return boxes.every(box => fitsRoyalMailDimensions(box))
}

function fitsParcelForceDimensions(box) {
  if (!box) return false
  const l = box.length || 0
  const w = box.width || 0
  const h = box.height || box.depth || 0
  const longest = Math.max(l, w, h)
  return longest <= 150
}

function allBoxesFitParcelForceDimensions(boxes) {
  if (!boxes || boxes.length === 0) return false
  return boxes.every(box => fitsParcelForceDimensions(box))
}

function getLongestSide(boxes) {
  if (!boxes || boxes.length === 0) return 0
  let longest = 0
  for (const box of boxes) {
    const maxSide = Math.max(box.length || 0, box.width || 0, box.height || box.depth || 0)
    if (maxSide > longest) longest = maxSide
  }
  return longest
}

function extractPostcodeFromAddress(address) {
  if (!address) return null
  const match = address.match(/[A-Z]{1,2}[0-9][0-9A-Z]?\s*[0-9][A-Z]{2}/i)
  return match ? match[0] : null
}

/**
 * Parse package_details string
 * Format: "Box 1: 14kg - 50 x 50 x 50 cm" or "Box 1: .5kg - 18 x 10 x 17 inches"
 */
function parsePackageDetails(packageString) {
  if (!packageString) return null

  const boxes = []
  // Accepts .5kg, 0.5kg, 5kg AND cm/inch/inches/in - dimensions can also be decimals
  const boxMatches = packageString.match(/Box\s*\d+:\s*(\.?\d+(?:\.\d+)?)\s*kg\s*-\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*(cm|inch|inches|in)/gi)

  if (boxMatches) {
    boxMatches.forEach((match, index) => {
      const parts = match.match(/(\.?\d+(?:\.\d+)?)\s*kg\s*-\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i)
      if (parts) {
        let weightStr = parts[1]
        if (weightStr.startsWith('.')) {
          weightStr = '0' + weightStr
        }

        // Convert inches to cm if needed
        const isInches = match.toLowerCase().includes('inch')
        const multiplier = isInches ? 2.54 : 1

        boxes.push({
          boxNumber: index + 1,
          weight: parseFloat(weightStr),
          length: Math.round(parseFloat(parts[2]) * multiplier),
          width: Math.round(parseFloat(parts[3]) * multiplier),
          height: Math.round(parseFloat(parts[4]) * multiplier)
        })
      }
    })
  }

  if (boxes.length === 0) return null

  // Calculate total weight
  const totalWeight = boxes.reduce((sum, box) => sum + (box.weight || 0), 0)

  return { boxes, totalWeight }
}

// Format number with leading zero and decimal places
function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '0'
  const num = parseFloat(value)
  if (isNaN(num)) return '0'
  // Ensure leading zero for decimals like .7 -> 0.7
  return num.toFixed(1).replace(/\.0$/, '')
}

function assignCarrier(deal) {
  const packageDetails = parsePackageDetails(deal.package_details)
  const postcode = extractPostcodeFromAddress(deal.collection_address)
  const boxes = packageDetails?.boxes || []
  const totalWeight = packageDetails?.totalWeight || boxes.reduce((sum, b) => sum + (b.weight || 0), 0) || 0
  // HubSpot stores has_printer as 'Yes'/'No' string
  const hasPrinter = deal.has_printer?.toLowerCase() === 'yes'
  const sendType = deal.send_type || 'courier'

  const boxCount = boxes.length || 1
  const isSingleBox = boxCount === 1
  const longestSide = getLongestSide(boxes)
  const isNI = isNorthernIreland(postcode)
  const isDropOff = sendType === 'dropoff'
  const rmWeightLimit = isNI ? 20 : 10
  const fitsRMWeight = totalWeight <= rmWeightLimit
  // Default to false when no boxes parsed (can't verify dimensions)
  const fitsRMDimensions = boxes.length > 0 ? allBoxesFitRoyalMailDimensions(boxes) : false
  const fitsRM = fitsRMWeight && fitsRMDimensions
  const fitsPF = boxes.length > 0 ? allBoxesFitParcelForceDimensions(boxes) : false
  const isOversized = longestSide > 61

  // STEP 1: SHIPLY for extreme cases (very heavy or very large)
  if (totalWeight >= 70 || longestSide > 150) {
    return { carrierId: 'shiply', reason: 'Exceeds standard limits (>70kg or >150cm)' }
  }

  // STEP 2: DROP-OFF FLOW
  if (isDropOff) {
    // Royal Mail: up to 10kg (20kg NI), max 61x46x46cm
    if (fitsRM) {
      return { carrierId: 'royal_mail', reason: 'Drop-off within RM limits' }
    }
    // ParcelForce: up to 30kg, max 150cm longest side, fits PF dimensions
    if (totalWeight <= 30 && fitsPF) {
      return { carrierId: 'parcelforce', reason: 'Drop-off within ParcelForce limits' }
    }
    // Too big/heavy for drop-off services
    return { carrierId: 'shiply', reason: 'Exceeds drop-off limits (>30kg or >150cm)', notAllowed: true }
  }

  // STEP 3: NORTHERN IRELAND
  if (isNI) {
    if (isSingleBox && fitsRM) {
      return { carrierId: 'royal_mail', reason: 'NI within RM limits' }
    }
    if (!hasPrinter && totalWeight <= 30) {
      return { carrierId: 'parcelforce', reason: 'NI no printer ≤30kg' }
    }
    return { carrierId: 'fedex', reason: 'NI with printer or heavy' }
  }

  // STEP 4a: OVERSIZED CHECK (>61cm longest side)
  if (isOversized) {
    if (hasPrinter) {
      return { carrierId: 'fedex', reason: 'Oversized with printer' }
    }
    if (totalWeight <= 30 && fitsPF) {
      return { carrierId: 'parcelforce', reason: 'Oversized no printer ≤30kg' }
    }
    return { carrierId: 'fedex', reason: 'Oversized heavy' }
  }

  // STEP 4b: MULTI-BOX
  if (!isSingleBox) {
    if (hasPrinter) {
      return { carrierId: 'fedex', reason: 'Multi-box with printer' }
    }
    if (totalWeight <= 30) {
      return { carrierId: 'parcelforce', reason: 'Multi-box no printer ≤30kg' }
    }
    return { carrierId: 'fedex', reason: 'Multi-box heavy' }
  }

  // STEP 4c: SINGLE BOX within RM dimensions
  if (fitsRM) {
    return { carrierId: 'royal_mail', reason: 'Within RM limits' }
  }
  if (!hasPrinter && totalWeight <= 20 && fitsRMDimensions) {
    return { carrierId: 'royal_mail', reason: '10-20kg QR code' }
  }
  if (hasPrinter && totalWeight <= 20) {
    return { carrierId: 'fedex', reason: '10-20kg with printer' }
  }
  if (!hasPrinter && totalWeight <= 30) {
    return { carrierId: 'parcelforce', reason: '20-30kg no printer' }
  }
  if (hasPrinter && totalWeight <= 30) {
    return { carrierId: 'fedex', reason: '20-30kg with printer' }
  }
  if (totalWeight > 30) {
    return { carrierId: 'fedex', reason: '>30kg' }
  }

  return { carrierId: 'fedex', reason: 'Fallback' }
}

// =============================================================================
// COMPONENTS
// =============================================================================

function ShipmentCard({ shipment, onClick, isSelected }) {
  const autoAssigned = assignCarrier(shipment)
  // Use saved carrier from HubSpot if exists (case-insensitive), otherwise use auto-assigned
  const hubspotCarrier = shipment.carrier?.toLowerCase()
  const carrierId = hubspotCarrier && CARRIERS[hubspotCarrier]
    ? hubspotCarrier
    : autoAssigned.carrierId
  const carrier = CARRIERS[carrierId]
  const pkg = parsePackageDetails(shipment.package_details)
  const totalWeight = pkg?.totalWeight || 0
  const boxCount = pkg?.boxes?.length || 1
  const isDropOff = shipment.send_type === 'dropoff'
  const hasPrinter = shipment.has_printer?.toLowerCase() === 'yes'

  return (
    <div
      onClick={onClick}
      style={{
        background: isSelected ? 'rgba(59, 178, 115, 0.1)' : 'white',
        border: isSelected ? '2px solid var(--green-onions)' : '1px solid var(--color-border)',
        borderRadius: 'var(--radius)',
        padding: 'var(--space-3)',
        marginBottom: 'var(--space-2)',
        cursor: 'pointer',
        transition: 'all 0.15s'
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 2 }}>
        {shipment.id}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {shipment.dealname || 'Unnamed Deal'}
        </div>
        <span style={{
          background: isDropOff ? 'var(--color-warning)' : 'var(--color-info)',
          color: isDropOff ? '#000' : '#fff',
          fontSize: 9,
          padding: '3px 8px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 4,
          fontWeight: 600,
          lineHeight: 1,
          textAlign: 'center'
        }}>
          {isDropOff ? 'DROP-OFF' : 'COURIER'}
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
        {shipment.contact_name || shipment.seller_email || '-'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="badge" style={{ background: carrier.color, color: 'white', fontSize: 10 }}>
          {carrier.name}
        </span>
        {hasPrinter && (
          <span className="badge" style={{ fontSize: 9 }}>Printer</span>
        )}
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {boxCount} box · {formatNumber(totalWeight)}kg
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
        {autoAssigned.reason}
      </div>
      {shipment.collection_date && (
        <div style={{ fontSize: 11, color: 'var(--color-info)', marginTop: 4, fontWeight: 500 }}>
          📅 {shipment.collection_date}
        </div>
      )}
    </div>
  )
}

function CarrierColumn({ carrierId, shipments, selectedId, onSelect }) {
  const carrier = CARRIERS[carrierId]
  return (
    <div style={{
      flex: '0 0 280px',
      background: 'var(--color-bg-secondary)',
      borderRadius: 'var(--radius)',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 280px)',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: 'var(--space-3)',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 12, height: 12, borderRadius: '50%',
            background: carrier.color
          }} />
          <span
            onClick={() => window.open(carrier.url, '_blank', 'width=1200,height=800')}
            style={{
              fontWeight: 600,
              color: 'inherit',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={e => e.target.style.textDecoration = 'underline'}
            onMouseLeave={e => e.target.style.textDecoration = 'none'}
            title={`Open ${carrier.name} shipping portal`}
          >
            {carrier.name}
          </span>
        </div>
        <span style={{
          background: '#333',
          color: 'white',
          padding: '4px 10px',
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600
        }}>{shipments.length}</span>
      </div>
      <div style={{ padding: 'var(--space-2)', flex: 1, overflowY: 'auto' }}>
        {shipments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
            No shipments
          </div>
        ) : (
          shipments.map(s => (
            <ShipmentCard
              key={s.id}
              shipment={s}
              isSelected={selectedId === s.id}
              onClick={() => onSelect(s)}
            />
          ))
        )}
      </div>
    </div>
  )
}

function QuickCopyRow({ label, value, onCopy, copied, mono }) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid var(--color-border)'
    }}>
      <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 13,
          fontWeight: mono ? 500 : 600,
          fontFamily: mono ? 'monospace' : 'inherit'
        }}>
          {value}
        </span>
        <button
          className="btn btn-xs"
          onClick={() => onCopy(value, label)}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            background: '#333',
            color: 'white',
            border: 'none'
          }}
        >
          {copied === label ? '✓' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

function DetailPanel({ shipment, onClose, onConfirm }) {
  const [trackingNumber, setTrackingNumber] = useState(shipment.tracking_number || '')
  const [shippingCost, setShippingCost] = useState(shipment.shipping_cost || '')
  const [collectionDate, setCollectionDate] = useState(shipment.collection_date || '')
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(null)

  // Contact info state - fetched separately to avoid subrequest limits
  const [contactInfo, setContactInfo] = useState({
    email: shipment.seller_email || null,
    name: shipment.contact_name || null
  })

  // Fetch contact info on mount
  useEffect(() => {
    async function fetchContact() {
      try {
        const res = await api.get(`/api/deals/collections/${shipment.id}/contact`)
        if (res.contact) {
          setContactInfo({
            email: res.contact.email || null,
            name: res.contact.firstName || null,
            contactId: res.contact.contactId
          })
        }
      } catch (e) {
        console.error('Failed to fetch contact:', e)
      }
    }
    if (!shipment.seller_email) {
      fetchContact()
    }
  }, [shipment.id])

  // FedEx label upload state
  const [labelFile, setLabelFile] = useState(null)
  const [uploadedLabelUrl, setUploadedLabelUrl] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  // Get auto-assigned carrier
  const autoAssigned = assignCarrier(shipment)

  // Use saved carrier from HubSpot if exists (case-insensitive), otherwise use auto-assigned
  const hubspotCarrier = shipment.carrier?.toLowerCase()
  const initialCarrier = hubspotCarrier && CARRIERS[hubspotCarrier]
    ? hubspotCarrier
    : autoAssigned.carrierId

  const [selectedCarrier, setSelectedCarrier] = useState(initialCarrier)
  const isManuallyChanged = selectedCarrier !== autoAssigned.carrierId
  const isFedEx = selectedCarrier === 'fedex'

  const carrier = CARRIERS[selectedCarrier]
  const pkg = parsePackageDetails(shipment.package_details)
  const boxCount = pkg?.boxes?.length || 1
  const isDropOff = shipment.send_type === 'dropoff'

  // Label upload required for FedEx (always) or drop-offs (any carrier)
  const needsLabelUpload = isFedEx || isDropOff

  // Calculate minimum date (tomorrow)
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const minDate = tomorrow.toISOString().split('T')[0]

  async function copyToClipboard(text, field) {
    await navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 1500)
  }

  // Handle file selection for FedEx label
  function handleFileSelect(e) {
    const file = e.target.files[0]
    if (file) {
      setLabelFile(file)
      setUploadError(null)
    }
  }

  // Upload label to R2
  async function handleUploadLabel() {
    if (!labelFile) return
    setIsUploading(true)
    setUploadError(null)

    try {
      const formData = new FormData()
      formData.append('file', labelFile)
      formData.append('dealId', shipment.id)

      const apiBase = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiBase}/api/r2/upload-label`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      const result = await response.json()
      if (result.success) {
        setUploadedLabelUrl(result.url)
        setLabelFile(null)
      } else {
        setUploadError(result.error || 'Upload failed')
      }
    } catch (err) {
      setUploadError('Upload failed: ' + err.message)
    } finally {
      setIsUploading(false)
    }
  }

  async function handleSaveCarrier() {
    // Save carrier only - no email, label can be added later
    setSaving(true)
    try {
      const result = await api.put(`/api/deals/collections/${shipment.id}/carrier`, {
        carrier: selectedCarrier,
        trackingNumber: trackingNumber || null,
        shippingCost: parseFloat(shippingCost) || 0,
        collectionDate: collectionDate || null,
        collectionAddress: shipment.collection_address,
        labelUrl: uploadedLabelUrl || null,
        boxCount,
        sendType: shipment.send_type,
        saveOnly: true
      })

      console.log('Save carrier result:', result)
      onConfirm()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleConfirm() {
    // Validation: require label upload for FedEx or drop-offs
    if (needsLabelUpload && !uploadedLabelUrl) {
      alert(`Please upload the shipping label before confirming`)
      return
    }

    setSaving(true)
    try {
      // Update carrier info - backend now also updates stage and sends email automatically
      const result = await api.put(`/api/deals/collections/${shipment.id}/carrier`, {
        carrier: selectedCarrier,
        trackingNumber,
        shippingCost: parseFloat(shippingCost) || 0,
        collectionDate,
        collectionAddress: shipment.collection_address,
        labelUrl: uploadedLabelUrl,
        boxCount,
        sendType: shipment.send_type
      })

      // Warn user if email failed to send
      if (result.emailSent === false) {
        alert(`Warning: Collection booked but the customer email failed to send.${result.emailError ? `\n\nReason: ${result.emailError}` : ''}\n\nPlease send the email manually or retry.`)
      }

      onConfirm()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }

  const CopyRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
      <div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{label}</div>
        <div style={{ fontSize: 13 }}>{value || '-'}</div>
      </div>
      {value && (
        <button
          className="btn btn-xs"
          onClick={() => copyToClipboard(value, label)}
          style={{
            fontSize: 11,
            background: '#333',
            color: 'white',
            border: 'none',
            padding: '4px 8px'
          }}
        >
          {copied === label ? 'Copied!' : 'Copy'}
        </button>
      )}
    </div>
  )

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: 420,
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ padding: 'var(--space-4)', background: carrier.color, color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            {/* Carrier Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <select
                value={selectedCarrier}
                onChange={e => setSelectedCarrier(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minWidth: 140
                }}
              >
                {Object.values(CARRIERS).map(c => (
                  <option key={c.id} value={c.id} style={{ color: '#333' }}>
                    {c.name}{c.id === autoAssigned.carrierId ? ' (Auto)' : ''}
                  </option>
                ))}
              </select>
              {isManuallyChanged && (
                <button
                  onClick={() => setSelectedCarrier(autoAssigned.carrierId)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>
              )}
              <button
                onClick={handleSaveCarrier}
                disabled={saving}
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Carrier'}
              </button>
            </div>
            {/* Status Tags */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{
                background: shipment.send_type === 'dropoff' ? '#FFD44D' : 'rgba(255,255,255,0.3)',
                color: shipment.send_type === 'dropoff' ? '#000' : '#fff',
                padding: '4px 10px',
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                {shipment.send_type === 'dropoff' ? '📍 Drop-off' : '🚚 Courier Collection'}
              </span>
              {shipment.has_printer?.toLowerCase() === 'yes' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  🖨️ Has Printer
                </span>
              )}
            </div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{shipment.dealname}</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>ID: {shipment.id}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>
            ×
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
        {/* Quick Copy Section */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>QUICK COPY</div>
          <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: '4px 12px' }}>
            <QuickCopyRow label="Reference" value={shipment.id} onCopy={copyToClipboard} copied={copied} mono />
            <QuickCopyRow label="Customer" value={contactInfo.name} onCopy={copyToClipboard} copied={copied} />
            <QuickCopyRow label="Phone" value={shipment.collection_phone} onCopy={copyToClipboard} copied={copied} mono />
            <QuickCopyRow label="Postcode" value={extractPostcodeFromAddress(shipment.collection_address)} onCopy={copyToClipboard} copied={copied} mono />
            <QuickCopyRow label="Email" value={contactInfo.email} onCopy={copyToClipboard} copied={copied} />
          </div>
        </div>

        {/* Carrier Reason */}
        <div style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-3)', borderRadius: 'var(--radius)', marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {isManuallyChanged ? 'Manual Override' : 'Auto-Assigned Carrier'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {isManuallyChanged
              ? `Changed from ${CARRIERS[autoAssigned.carrierId]?.name || 'Unknown'} to ${carrier.name}`
              : autoAssigned.reason}
          </div>
        </div>

        {/* Address */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>
            {shipment.send_type === 'dropoff' ? 'DROP-OFF ADDRESS' : 'COLLECTION ADDRESS'}
          </div>
          <CopyRow label="Full Address" value={shipment.collection_address} />
          <CopyRow label={shipment.send_type === 'dropoff' ? 'Drop-off Date' : 'Collection Date'} value={shipment.collection_date} />
        </div>

        {/* Package */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>PACKAGE</div>
          {pkg ? (
            <>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                {pkg.boxes?.length || 1} box(es) · {formatNumber(pkg.totalWeight || 0)}kg total
              </div>
              {pkg.boxes?.map((box, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Box {i+1}: {formatNumber(box.weight)}kg - {formatNumber(box.length)}×{formatNumber(box.width)}×{formatNumber(box.height)}cm
                </div>
              ))}
            </>
          ) : shipment.package_details ? (
            <div style={{
              background: 'var(--color-bg-secondary)',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius)',
              fontSize: 12,
              whiteSpace: 'pre-line',
              lineHeight: 1.8
            }}>
              {shipment.package_details.replace(/\s*(Box\s*\d+)/gi, '\n$1').trim()}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No package details</div>
          )}
        </div>

        {/* Label Upload - for FedEx or Drop-offs */}
        {needsLabelUpload && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>
              {isDropOff ? 'DROP-OFF LABEL' : 'FEDEX LABEL'} {boxCount > 1 ? `(${boxCount} BOXES)` : ''} <span style={{ color: 'var(--color-error)' }}>*</span>
            </div>

            {/* Uploaded label */}
            {uploadedLabelUrl && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: 'rgba(59, 178, 115, 0.1)',
                border: '1px solid var(--green-onions)',
                borderRadius: 'var(--radius)',
                marginBottom: 8
              }}>
                <span style={{ color: 'var(--green-onions)', fontSize: 16 }}>✓</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
                  {isDropOff ? 'Drop-off Label' : 'FedEx Label'}{boxCount > 1 ? ` (${boxCount} boxes)` : ''}
                </span>
                <a
                  href={uploadedLabelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-info)', fontSize: 12, textDecoration: 'none' }}
                >
                  View
                </a>
                <button
                  onClick={() => setUploadedLabelUrl(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: 18 }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Pending file */}
            {labelFile && !uploadedLabelUrl && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius)',
                marginBottom: 8
              }}>
                <span style={{ fontSize: 16 }}>📎</span>
                <span style={{ flex: 1, fontSize: 13 }}>{labelFile.name}</span>
                <button
                  onClick={() => setLabelFile(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: 18 }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Upload button */}
            {labelFile && !uploadedLabelUrl && (
              <button
                className="btn btn-secondary"
                onClick={handleUploadLabel}
                disabled={isUploading}
                style={{ width: '100%', marginBottom: 8 }}
              >
                {isUploading ? 'Uploading...' : 'Upload Label'}
              </button>
            )}

            {/* File input dropzone */}
            {!uploadedLabelUrl && !labelFile && (
              <div style={{
                border: '2px dashed var(--color-border)',
                borderRadius: 'var(--radius)',
                padding: 24,
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative'
              }}>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={handleFileSelect}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                />
                <div style={{ fontSize: 24, marginBottom: 8 }}>📎</div>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  Upload {isDropOff ? 'drop-off' : 'shipping'} label
                </p>
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {boxCount > 1 ? `PDF with all ${boxCount} box labels` : 'PDF or image'}
                </p>
              </div>
            )}

            {/* Upload error */}
            {uploadError && (
              <div style={{
                marginTop: 8,
                padding: 12,
                background: 'rgba(231, 76, 60, 0.1)',
                borderRadius: 'var(--radius)',
                color: 'var(--color-danger)',
                fontSize: 12
              }}>
                {uploadError}
              </div>
            )}
          </div>
        )}

        {/* Tracking & Cost */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>SHIPMENT DETAILS</div>
          <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
            <label style={{ fontSize: 12 }}>Tracking Number <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="text"
              className="form-input"
              value={trackingNumber}
              onChange={e => setTrackingNumber(extractTrackingNumber(e.target.value))}
              onPaste={e => {
                e.preventDefault()
                const pasted = e.clipboardData.getData('text')
                setTrackingNumber(extractTrackingNumber(pasted))
              }}
              placeholder="Enter or paste tracking number/URL"
            />
            {trackingNumber && (
              <div style={{ marginTop: 4 }}>
                {trackingNumber.split(',').map((tn, idx) => {
                  const trimmed = tn.trim()
                  if (!trimmed) return null
                  const url = getTrackingUrl(trimmed, selectedCarrier)
                  if (!url) return null
                  return (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 11, color: 'var(--color-info)', display: 'block', marginTop: idx > 0 ? 2 : 0 }}
                    >
                      Track: {trimmed} →
                    </a>
                  )
                })}
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
            <label style={{ fontSize: 12 }}>Shipping Cost (£) <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="number"
              step="0.01"
              className="form-input"
              value={shippingCost}
              onChange={e => setShippingCost(e.target.value)}
              placeholder="0.00"
            />
          </div>
          {!isDropOff && (
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Collection Date</label>
              <input
                type="date"
                className="form-input"
                value={collectionDate}
                onChange={e => setCollectionDate(e.target.value)}
                min={minDate}
              />
            </div>
          )}
        </div>

        {/* Items */}
        {shipment.parsed_items && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>ITEMS</div>
            <div style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-2)', borderRadius: 'var(--radius)', fontSize: 12 }}>
              {shipment.parsed_items.split('\n').filter(l => l.trim()).map((item, i) => (
                <div key={i} style={{ padding: '4px 0' }}>{item}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: 'var(--space-4)', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 'var(--space-2)' }}>
        <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>
          Cancel
        </button>
        {(() => {
          const missingFields = []
          if (!trackingNumber) missingFields.push('Tracking Number')
          if (!shippingCost) missingFields.push('Shipping Cost')
          if (needsLabelUpload && !uploadedLabelUrl) missingFields.push('Label Upload')
          const isDisabled = saving || missingFields.length > 0

          const handleClick = () => {
            if (missingFields.length > 0) {
              alert(`Please enter the following required fields:\n\n• ${missingFields.join('\n• ')}`)
              return
            }
            handleConfirm()
          }

          return (
            <button
              className="btn btn-primary"
              onClick={handleClick}
              style={{
                flex: 1,
                opacity: isDisabled ? 0.4 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                background: isDisabled ? 'var(--color-border)' : undefined
              }}
            >
              {saving ? 'Processing...' : 'Confirm & Book'}
            </button>
          )
        })()}
      </div>
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function Collections() {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ pending: 0, scheduled: 0, problemCount: 0, bookedToday: 0 })
  const [selectedShipment, setSelectedShipment] = useState(null)
  const [generatingToken, setGeneratingToken] = useState(null)
  const [generatedLink, setGeneratedLink] = useState(null)

  // Track recently booked deal IDs to filter them out on reload
  const [recentlyBookedIds, setRecentlyBookedIds] = useState(new Set())

  // For clickable KPIs
  const [bookedTodayDeals, setBookedTodayDeals] = useState([])
  const [problemDeals, setProblemDeals] = useState([])
  const [resendingEmail, setResendingEmail] = useState(null)
  const [showKpiModal, setShowKpiModal] = useState(null) // 'bookedToday' or 'problem'

  useEffect(() => {
    loadCollections()
  }, [])

  async function loadCollections() {
    setLoading(true)
    try {
      // Fetch pending, scheduled, and problem deals
      const [pendingRes, scheduledRes, problemRes] = await Promise.all([
        api.get('/api/deals/collections?view=pending'),
        api.get('/api/deals/collections?view=scheduled'),
        api.get('/api/deals/collections?view=problem')
      ])

      // Combine pending deals for the main view, filtering out recently booked
      const pendingDeals = (pendingRes.deals || []).filter(d => !recentlyBookedIds.has(d.id))
      setDeals(pendingDeals)

      // Store problem deals
      setProblemDeals(problemRes.deals || [])

      // Calculate booked today from BOTH pending and scheduled deals
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const allDeals = [...(pendingRes.deals || []), ...(scheduledRes.deals || [])]

      const isBookedToday = (d) => {
        if (!d.collection_booked_date) return false

        const dateVal = d.collection_booked_date
        let bookedDate

        // Handle epoch timestamp (HubSpot often returns dates as milliseconds)
        if (typeof dateVal === 'number' || /^\d{13}$/.test(dateVal)) {
          bookedDate = new Date(parseInt(dateVal))
        } else if (/^\d{10}$/.test(dateVal)) {
          // Unix seconds
          bookedDate = new Date(parseInt(dateVal) * 1000)
        } else if (typeof dateVal === 'string') {
          if (dateVal.includes('T')) {
            // ISO format
            bookedDate = new Date(dateVal)
          } else if (dateVal.includes('/')) {
            // UK format DD/MM/YYYY
            const [day, month, year] = dateVal.split('/')
            bookedDate = new Date(year, month - 1, day)
          } else if (dateVal.includes('-')) {
            // YYYY-MM-DD
            bookedDate = new Date(dateVal)
          }
        }

        if (!bookedDate || isNaN(bookedDate.getTime())) return false

        // Compare dates (ignore time)
        bookedDate.setHours(0, 0, 0, 0)
        return bookedDate.getTime() === today.getTime()
      }

      const todayDeals = allDeals.filter(isBookedToday)
      setBookedTodayDeals(todayDeals)

      if (pendingRes.stats) {
        setStats({
          ...pendingRes.stats,
          bookedToday: todayDeals.length,
          problemCount: problemRes.deals?.length || 0
        })
      }
    } catch (error) {
      console.error('Failed to load collections:', error)
    } finally {
      setLoading(false)
    }
  }

  async function resendLabelEmail(dealId) {
    setResendingEmail(dealId)
    try {
      const result = await api.post('/api/deals/collections/send-label-email', { dealId })
      if (result.emailSent) {
        alert('Email sent successfully!')
      } else {
        alert(`Failed to send email.${result.emailError ? `\n\nReason: ${result.emailError}` : ''}`)
      }
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setResendingEmail(null)
    }
  }

  async function generateCollectionLink(dealId, contactId) {
    setGeneratingToken(dealId)
    try {
      const response = await api.post('/api/deals/collections/generate-token', { contactId, dealId })
      setGeneratedLink(response.formUrl)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setGeneratingToken(null)
    }
  }

  // Group shipments by assigned carrier
  const shipmentsByCarrier = {
    royal_mail: [],
    parcelforce: [],
    fedex: [],
    shiply: []
  }

  const dealsWithAddress = deals.filter(d => d.collection_address)
  const dealsWithoutAddress = deals.filter(d => !d.collection_address)

  dealsWithAddress.forEach(deal => {
    // Use saved carrier from HubSpot if exists (case-insensitive), otherwise use auto-assigned
    const autoAssigned = assignCarrier(deal)
    const hubspotCarrier = deal.carrier?.toLowerCase()
    const carrierId = hubspotCarrier && CARRIERS[hubspotCarrier]
      ? hubspotCarrier
      : autoAssigned.carrierId
    if (shipmentsByCarrier[carrierId]) {
      shipmentsByCarrier[carrierId].push(deal)
    }
  })

  // Sort each carrier's shipments by collection_date (earliest first)
  Object.keys(shipmentsByCarrier).forEach(carrier => {
    shipmentsByCarrier[carrier].sort((a, b) => {
      if (!a.collection_date) return 1
      if (!b.collection_date) return -1
      return new Date(a.collection_date) - new Date(b.collection_date)
    })
  })

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <>
      {/* Stats */}
      <div className="statsRow">
        <div
          className="statBox"
          style={{ cursor: stats.bookedToday > 0 ? 'pointer' : 'default' }}
          onClick={() => stats.bookedToday > 0 && setShowKpiModal('bookedToday')}
        >
          <div className="statValue">{stats.bookedToday || 0}</div>
          <div className="statLabel">Booked Today</div>
        </div>
        <div className="statBox active">
          <div className="statValue">{stats.pending}</div>
          <div className="statLabel">Pending Label</div>
        </div>
        <div
          className="statBox"
          style={{ cursor: stats.problemCount > 0 ? 'pointer' : 'default', background: stats.problemCount > 0 ? 'rgba(231, 76, 60, 0.1)' : undefined }}
          onClick={() => stats.problemCount > 0 && setShowKpiModal('problem')}
        >
          <div className="statValue" style={{ color: stats.problemCount > 0 ? 'var(--color-danger)' : undefined }}>{stats.problemCount || 0}</div>
          <div className="statLabel">Problem with Shipping</div>
        </div>
        <div className="statBox">
          <div className="statValue">{dealsWithoutAddress.length}</div>
          <div className="statLabel">No Collection Link</div>
        </div>
      </div>

      {/* Deals without collection address */}
      {dealsWithoutAddress.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid var(--color-border)', fontWeight: 600 }}>
            Awaiting Collection Link ({dealsWithoutAddress.length})
          </div>
          <div style={{ padding: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {dealsWithoutAddress.map(deal => (
              <div key={deal.id} style={{
                background: 'var(--color-bg-secondary)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}>
                <span style={{ fontSize: 13 }}>{deal.dealname || deal.id}</span>
                {deal.contact_id && (
                  <button
                    className="btn btn-xs btn-primary"
                    onClick={() => generateCollectionLink(deal.id, deal.contact_id)}
                    disabled={generatingToken === deal.id}
                  >
                    {generatingToken === deal.id ? '...' : 'Generate'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Carrier Swimlanes */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        overflowX: 'auto',
        paddingBottom: 'var(--space-4)'
      }}>
        <CarrierColumn
          carrierId="royal_mail"
          shipments={shipmentsByCarrier.royal_mail}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
        />
        <CarrierColumn
          carrierId="parcelforce"
          shipments={shipmentsByCarrier.parcelforce}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
        />
        <CarrierColumn
          carrierId="fedex"
          shipments={shipmentsByCarrier.fedex}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
        />
        <CarrierColumn
          carrierId="shiply"
          shipments={shipmentsByCarrier.shiply}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
        />
      </div>

      {/* Detail Panel */}
      {selectedShipment && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 999
            }}
            onClick={() => setSelectedShipment(null)}
          />
          <DetailPanel
            shipment={selectedShipment}
            onClose={() => setSelectedShipment(null)}
            onConfirm={() => {
              // Optimistically remove the deal from local state immediately
              const bookedDealId = selectedShipment.id

              // Add to recently booked set to prevent it coming back on reload
              setRecentlyBookedIds(prev => new Set([...prev, bookedDealId]))

              // Clear from recently booked after 60 seconds (HubSpot should have updated by then)
              setTimeout(() => {
                setRecentlyBookedIds(prev => {
                  const next = new Set(prev)
                  next.delete(bookedDealId)
                  return next
                })
              }, 60000)

              setDeals(prev => prev.filter(d => d.id !== bookedDealId))
              setStats(prev => ({
                ...prev,
                pending: Math.max(0, prev.pending - 1),
                bookedToday: prev.bookedToday + 1
              }))
              setSelectedShipment(null)
              // No auto-reload - optimistic update handles UI, user can manually refresh if needed
            }}
          />
        </>
      )}

      {/* Generated Link Modal */}
      {generatedLink && (
        <div className="modal-overlay" onClick={() => setGeneratedLink(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Collection Link Generated</h2>
              <button className="modal-close" onClick={() => setGeneratedLink(null)}>×</button>
            </div>
            <div className="modal-body-scroll">
              <p>Send this link to the customer:</p>
              <div style={{
                background: 'var(--color-bg-secondary)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius)',
                wordBreak: 'break-all',
                marginTop: 'var(--space-3)'
              }}>
                {generatedLink}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setGeneratedLink(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => {
                navigator.clipboard.writeText(generatedLink)
                alert('Link copied!')
              }}>
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Details Modal */}
      {showKpiModal && (
        <div className="modal-overlay" onClick={() => setShowKpiModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h2>{showKpiModal === 'bookedToday' ? 'Booked Today' : 'Problem with Shipping'}</h2>
              <button className="modal-close" onClick={() => setShowKpiModal(null)}>×</button>
            </div>
            <div className="modal-body-scroll" style={{ maxHeight: 400 }}>
              {(showKpiModal === 'bookedToday' ? bookedTodayDeals : problemDeals).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
                  No deals found
                </div>
              ) : (
                <table className="table" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>Deal ID</th>
                      <th>Name</th>
                      <th>Collection Date</th>
                      <th>Carrier</th>
                      {showKpiModal === 'bookedToday' && <th>Email</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(showKpiModal === 'bookedToday' ? bookedTodayDeals : problemDeals).map(deal => {
                      const autoCarrier = assignCarrier(deal)
                      const hubspotCarrier = deal.carrier?.toLowerCase()
                      const carrierId = hubspotCarrier && CARRIERS[hubspotCarrier] ? hubspotCarrier : autoCarrier.carrierId
                      const carrier = CARRIERS[carrierId]
                      return (
                        <tr
                          key={deal.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setShowKpiModal(null)
                            setSelectedShipment(deal)
                          }}
                        >
                          <td style={{ fontFamily: 'monospace' }}>{deal.id}</td>
                          <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {deal.dealname || '-'}
                          </td>
                          <td>{deal.collection_date || '-'}</td>
                          <td>
                            <span style={{
                              background: carrier?.color || '#999',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: 4,
                              fontSize: 11
                            }}>
                              {carrier?.name || 'Unknown'}
                            </span>
                          </td>
                          {showKpiModal === 'bookedToday' && (
                            <td>
                              <button
                                className="btn btn-xs"
                                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                disabled={resendingEmail === deal.id}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (confirm(`Resend label email for ${deal.dealname || deal.id}?`)) {
                                    resendLabelEmail(deal.id)
                                  }
                                }}
                              >
                                {resendingEmail === deal.id ? 'Sending...' : 'Resend Email'}
                              </button>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowKpiModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Collections
